// src/components/ShareDialog.jsx
import React, { useEffect, useState } from 'react'
import {
  FacebookFilled,
  TwitterCircleFilled,
  InstagramFilled,
  WhatsAppOutlined,
  CopyOutlined,
} from '@ant-design/icons'
import tryShorten from '../utils/urlShorten'



const IconAction = ({ label, onClick, Icon, title }) => (
  <div className="flex flex-col items-center">
    <button
      onClick={onClick}
      title={title || label}
      aria-label={title || label}
      className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center hover:shadow-md transition"
    >
      <Icon className="text-3xl text-gray-700" />
    </button>
    <div className="mt-2 text-xs text-gray-700">{label}</div>
  </div>
)

/**
 * ShareDialog (centered modal with blurred background)
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - title: string
 * - description: string
 * - url: string (route to shorten/share)
 * - image: string (optional absolute image url)
 */
const ShareDialog = ({ open, onClose, title = '', description = '', url = '', image = '' }) => {
  const [shortUrl, setShortUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)

  // canonical route to shorten (ensures full URL)
  const canonical = url ? (url.startsWith('http') ? url : `${window.location.origin}${url}`) : window.location.href

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    tryShorten(canonical)
      .then(s => { if (!cancelled) setShortUrl(s) })
      .catch(() => { if (!cancelled) setShortUrl(canonical) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, canonical])

  const getLink = () => shortUrl || canonical

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getLink())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }

  const shareWhatsApp = () => {
    const msg = [title, description, getLink()].filter(Boolean).join('\n\n')
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, '_blank')
    onClose && onClose()
  }

  const shareFacebook = () => {
    const quote = [title, description].filter(Boolean).join('\n\n')
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getLink())}&quote=${encodeURIComponent(quote)}`, '_blank')
    onClose && onClose()
  }

  const shareTwitter = () => {
    const text = [title, description].filter(Boolean).join(' - ')
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(getLink())}`, '_blank')
    onClose && onClose()
  }

  const shareInstagram = async () => {
    try {
      const parts = [title, description, getLink()]
      if (image) parts.push(image)
      await navigator.clipboard.writeText(parts.filter(Boolean).join('\n\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
    window.open('https://www.instagram.com/', '_blank')
    onClose && onClose()
  }

  if (!open) return null

  return (
    // modal container centered
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* blurred overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* dialog */}
      <div className="relative z-10 w-[720px] max-w-[95%] bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* header (removed title as requested; keep close button) */}
        <div className="px-6 pt-3 flex items-center justify-end bg-white">
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-700 text-xl opacity-90 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#1f9c38] rounded"
          >
            ✕
          </button>
        </div>

        {/* body */}
        <div className="p-6">
          {/* icons grid */}
          <div className="grid grid-cols-4 gap-x-8 gap-y-6 justify-items-center">
            <IconAction label="Facebook" Icon={FacebookFilled} onClick={shareFacebook} />
            <IconAction label="Twitter" Icon={TwitterCircleFilled} onClick={shareTwitter} />
            <IconAction label="WhatsApp" Icon={WhatsAppOutlined} onClick={shareWhatsApp} />
            <IconAction label="Instagram" Icon={InstagramFilled} onClick={shareInstagram} />
          </div>

          {/* copy link row */}
          <div className="mt-6">
            <div className="text-sm text-gray-600 mb-2">Or copy link</div>

            <div className="flex items-center gap-3">
              <input
                readOnly
                value={loading ? 'Shortening...' : getLink()}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 truncate"
                aria-label="Share link"
              />
              <button
                onClick={copyLink}
                className="px-4 py-3 rounded-lg bg-[var(--color-primary)] text-white flex items-center gap-2 hover:bg-[#18812f] transition"
                aria-label="Copy link"
              >
                <CopyOutlined />
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShareDialog
